/**
 * Getting a project's files back after a reload.
 *
 * Three ways in, all ending in a ResolvedSource: a stored handle the user lets
 * us read again, a file chosen for one specific source, or a pile of files
 * matched to whatever is missing by fingerprint and then by name. Nothing here
 * refuses a file; the match quality travels with the result so the UI can say
 * what it thinks.
 */
import { ACCEPT, fingerprintFile, matchQuality, type MatchQuality, type ResolvedSource } from "./sources.ts";
import type { SourceRef } from "./timeline.ts";

export interface Picked {
  file: File;
  handle?: FileSystemFileHandle;
}

export interface Supplied {
  resolved: ResolvedSource;
  quality: MatchQuality;
}

/** Use this file for this source, whatever it looks like. */
export async function supply(ref: SourceRef, picked: Picked): Promise<Supplied> {
  const fingerprint = await fingerprintFile(picked.file);
  return {
    resolved: {
      ref,
      file: picked.file,
      url: URL.createObjectURL(picked.file),
      ...(picked.handle ? { handle: picked.handle } : {}),
    },
    quality: matchQuality(ref, fingerprint, picked.file),
  };
}

/**
 * Hand out a pile of files to the sources still missing: exact fingerprint
 * first, then same name. A file that fits nothing is returned, not guessed at,
 * even when only one source is left: that is the user's call to make.
 */
export async function assign(
  missing: SourceRef[],
  picked: Picked[],
): Promise<{ matched: Map<string, Supplied>; unmatched: File[] }> {
  const matched = new Map<string, Supplied>();
  const unmatched: File[] = [];
  const open = new Map(missing.map((ref) => [ref.id, ref]));
  const prints = await Promise.all(picked.map((item) => fingerprintFile(item.file)));

  const take = (ref: SourceRef, item: Picked, quality: MatchQuality) => {
    open.delete(ref.id);
    matched.set(ref.id, {
      resolved: { ref, file: item.file, url: URL.createObjectURL(item.file), ...(item.handle ? { handle: item.handle } : {}) },
      quality,
    });
  };

  const leftover: Picked[] = [];
  picked.forEach((item, index) => {
    const exact = [...open.values()].find((ref) => ref.fingerprint === prints[index]);
    if (exact) take(exact, item, "exact");
    else leftover.push(item);
  });
  for (const item of leftover) {
    const byName = [...open.values()].find((ref) => ref.name === item.file.name);
    if (byName) take(byName, item, "changed");
    else unmatched.push(item.file);
  }
  return { matched, unmatched };
}

export type HandleOutcome = "granted" | "denied" | "gone";

/** The permission methods are Chromium-only and not in the TypeScript lib yet. */
type PermissionedHandle = FileSystemFileHandle & {
  queryPermission(descriptor: { mode: "read" | "readwrite" }): Promise<PermissionState>;
  requestPermission(descriptor: { mode: "read" | "readwrite" }): Promise<PermissionState>;
};

/**
 * Reopen sources from stored handles. Each needs the user's permission again,
 * which the browser asks for itself; whether one click covers several files is
 * the browser's decision, so this stops asking once a request is refused for
 * want of a user gesture and reports what it managed.
 */
export async function reopen(
  missing: SourceRef[],
  handles: Map<string, FileSystemFileHandle>,
  onEach?: (ref: SourceRef, outcome: HandleOutcome) => void,
): Promise<Map<string, Supplied>> {
  const matched = new Map<string, Supplied>();
  for (const ref of missing) {
    const handle = handles.get(ref.id) as PermissionedHandle | undefined;
    if (!handle) continue;
    try {
      let permission = await handle.queryPermission({ mode: "read" });
      if (permission !== "granted") permission = await handle.requestPermission({ mode: "read" });
      if (permission !== "granted") {
        onEach?.(ref, "denied");
        continue;
      }
      const file = await handle.getFile();
      matched.set(ref.id, await supply(ref, { file, handle }));
      onEach?.(ref, "granted");
    } catch (error) {
      // NotFoundError: the file moved. SecurityError: no user gesture left to
      // ask with. Either way the row stays open for the user to fill by hand.
      onEach?.(ref, (error as DOMException)?.name === "NotFoundError" ? "gone" : "denied");
      if ((error as DOMException)?.name === "SecurityError") break;
    }
  }
  return matched;
}

type PickerWindow = Window & {
  showOpenFilePicker?: (options: {
    multiple?: boolean;
    types?: { description: string; accept: Record<string, string[]> }[];
    excludeAcceptAllOption?: boolean;
  }) => Promise<FileSystemFileHandle[]>;
};

const MEDIA_TYPES = [
  {
    description: "Video, audio and pictures",
    accept: {
      "video/*": [".mp4", ".m4v", ".mov", ".webm", ".mkv"],
      "audio/*": [".mp3", ".m4a", ".aac", ".wav", ".ogg", ".flac", ".opus"],
      "image/*": [".png", ".jpg", ".jpeg", ".webp", ".gif", ".avif", ".bmp", ".svg"],
    },
  },
];

/**
 * Ask for files. Through the File System Access picker where there is one,
 * because only that hands out handles, so only that lets a project reopen
 * its files later; through a plain input everywhere else.
 */
export function pickMedia(options: { multiple?: boolean; accept?: string } = {}): Promise<Picked[]> {
  const picker = (window as PickerWindow).showOpenFilePicker;
  if (picker && !options.accept) {
    return picker({ multiple: options.multiple ?? true, types: MEDIA_TYPES })
      .then((handles) => Promise.all(handles.map(async (handle) => ({ file: await handle.getFile(), handle }))))
      .catch(() => []);
  }
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = options.accept ?? ACCEPT;
    input.multiple = options.multiple ?? true;
    input.style.display = "none";
    input.onchange = () => {
      resolve([...(input.files ?? [])].map((file) => ({ file })));
      input.remove();
    };
    input.oncancel = () => {
      resolve([]);
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  });
}
